import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Site, SiteProvisioningKind, SiteProvisioningProps } from '@ixicare/resources';

const queryKey = (siteIdOrUuid?: number | string) => ['site-provisioning', siteIdOrUuid];

/**
 * Whether a site is a warehouse (or homecare) site.
 *
 * It no longer decides what is counted — hardware counts wherever it sits, and a warehouse
 * is Ixi's own site. Everyone may read the flag; only Ixi employees may change it, which the
 * backend enforces and `mayEdit` reflects.
 */
export const useSiteProvisioning = (siteIdOrUuid?: number | string) => {
  const queryClient = useQueryClient();

  const query = useQuery<SiteProvisioningProps>({
    queryKey: queryKey(siteIdOrUuid),
    queryFn: () => Site.getSiteProvisioning(siteIdOrUuid!),
    enabled: !!siteIdOrUuid,
    staleTime: 5 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: (provisioning: SiteProvisioningKind | null) => Site.putSiteProvisioning(siteIdOrUuid!, provisioning),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey(siteIdOrUuid), data);
    },
  });

  return {
    provisioning: query.data?.provisioning ?? null,
    isProvisioningSite: !!query.data?.isProvisioningSite,
    mayEdit: !!query.data?.mayEdit,
    isLoading: query.isLoading,
    setProvisioning: mutation,
  };
};
